'use client';

import {
  cloneElement,
  createContext,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactElement,
} from 'react';
import { ChevronLeft, ChevronRight, Pause, Play } from 'lucide-react';
import { useIsCompactTouch } from '@/hooks/use-compact-touch';
import { cn } from '@/lib/utils';
import { useAutoplayInView } from '@/hooks/use-autoplay-in-view';
import CarouselButton from '@/components/shared/carousel-button';
import { VA_EVENTS } from '@/lib/analytics/event-catalog';
import { vaTrack } from '@/lib/analytics/vercel-events';
import { type ProjectCategoryType, type ProjectStatusType } from '@/sanity/project-data';

export interface ProjectDetailImage {
  src: string;
  alt?: string | null;
  title?: string | null;
}

export interface ProjectDetailMediaSection {
  id: string;
  label: string;
  images: ProjectDetailImage[];
}

export interface ProjectDetailBeforeAfterItem {
  title?: string | null;
  before: ProjectDetailImage;
  after: ProjectDetailImage;
}

export interface ProjectsParallaxItem {
  src: string;
  alt?: string | null;
  title: string;
  location?: string;
  type?: ProjectCategoryType | null;
  description?: string;
  delivered?: string;
  duration?: string;
  status?: ProjectStatusType;
  href?: string;
  mediaSections?: ProjectDetailMediaSection[];
  beforeAfter?: ProjectDetailBeforeAfterItem[];
}

export interface RecentWorkCardItem {
  src: string;
  alt?: string | null;
  title: string;
  location?: string;
  type?: ProjectCategoryType | null;
  description?: string;
  delivered?: string;
  duration?: string;
  status?: ProjectStatusType;
  content?: React.ReactNode;
  mediaSections?: ProjectDetailMediaSection[];
  beforeAfter?: ProjectDetailBeforeAfterItem[];
}

interface CarouselContextValue {
  onCardClose: (index: number) => void;
  currentIndex: number;
  /** Call with `true` when a slide opens a modal overlay; `false` when it closes (balance increments/decrements). */
  suspendAutoplayForOverlay: (pause: boolean) => void;
}

export const CarouselContext = createContext<CarouselContextValue>({
  onCardClose: () => {},
  currentIndex: 0,
  suspendAutoplayForOverlay: () => {},
});

export type CarouselSlideElement = ReactElement<{ instanceKey?: string }>;

interface CarouselProps {
  items: CarouselSlideElement[];
  initialScroll?: number;
  showControls?: boolean;
  enableAutoplay?: boolean;
}

function isCompactWidth() {
  return typeof window !== 'undefined' && window.innerWidth < 1024;
}

export function Carousel({
  items,
  initialScroll = 0,
  showControls = true,
  enableAutoplay = true,
}: CarouselProps) {
  const carouselRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const scrollEndTimeoutRef = useRef<number | null>(null);
  const isCompactTouch = useIsCompactTouch();
  const { ref: autoplayViewportRef, isInView: isAutoplayInView } = useAutoplayInView();
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [overlayPauseCount, setOverlayPauseCount] = useState(0);
  const shouldLoop = false;
  const isAutoplayActive =
    enableAutoplay && isPlaying && !isCompactTouch && overlayPauseCount === 0 && isAutoplayInView;

  const suspendAutoplayForOverlay = useCallback((pause: boolean) => {
    setOverlayPauseCount((c) => {
      const next = pause ? c + 1 : c - 1;
      return Math.max(0, next);
    });
  }, []);

  const checkScrollability = useCallback(() => {
    if (carouselRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    }
  }, []);

  const getLoopSpan = useCallback(() => {
    const track = trackRef.current;
    if (!track || items.length <= 1) return 0;

    const firstSlide = track.children[0] as HTMLElement | undefined;
    const firstDuplicate = track.children[items.length] as HTMLElement | undefined;

    if (!firstSlide || !firstDuplicate) return 0;

    return firstDuplicate.offsetLeft - firstSlide.offsetLeft;
  }, [items.length]);

  const normalizeScrollPosition = useCallback(() => {
    const carousel = carouselRef.current;
    const loopSpan = getLoopSpan();

    if (!carousel || loopSpan <= 0) return;

    if (carousel.scrollLeft < loopSpan) {
      carousel.scrollLeft += loopSpan;
    } else if (carousel.scrollLeft >= loopSpan * 2) {
      carousel.scrollLeft -= loopSpan;
    }
  }, [getLoopSpan]);

  const scheduleNormalizeScrollPosition = useCallback(() => {
    if (!shouldLoop) {
      checkScrollability();
      return;
    }

    if (scrollEndTimeoutRef.current) {
      window.clearTimeout(scrollEndTimeoutRef.current);
    }

    scrollEndTimeoutRef.current = window.setTimeout(() => {
      normalizeScrollPosition();
    }, 180);
  }, [checkScrollability, normalizeScrollPosition, shouldLoop]);

  useEffect(() => {
    const carousel = carouselRef.current;
    if (!carousel) return;

    const frame = window.requestAnimationFrame(() => {
      const loopSpan = getLoopSpan();
      carousel.scrollLeft = (shouldLoop && loopSpan > 0 ? loopSpan : 0) + initialScroll;
      if (shouldLoop) {
        setCanScrollLeft(true);
        setCanScrollRight(true);
      } else {
        checkScrollability();
      }
    });

    return () => window.cancelAnimationFrame(frame);
  }, [initialScroll, checkScrollability, getLoopSpan, shouldLoop]);

  useEffect(() => {
    return () => {
      if (scrollEndTimeoutRef.current) {
        window.clearTimeout(scrollEndTimeoutRef.current);
      }
    };
  }, []);

  const getSlideDistance = () => {
    const firstSlide = trackRef.current?.firstElementChild as HTMLElement | null;
    const secondSlide = firstSlide?.nextElementSibling as HTMLElement | null;

    if (firstSlide && secondSlide) {
      return secondSlide.offsetLeft - firstSlide.offsetLeft;
    }

    return firstSlide?.getBoundingClientRect().width ?? 380;
  };

  const scrollLeft = () => {
    if (!shouldLoop && !canScrollLeft) return;

    vaTrack(VA_EVENTS.CAROUSEL_SCROLL, { direction: 'left', section: 'recent_works' });
    carouselRef.current?.scrollBy({ left: -getSlideDistance(), behavior: 'smooth' });
  };

  const scrollRight = useCallback(() => {
    const carousel = carouselRef.current;
    if (!carousel) return;

    if (shouldLoop || canScrollRight) {
      vaTrack(VA_EVENTS.CAROUSEL_SCROLL, { direction: 'right', section: 'recent_works' });
      carousel.scrollBy({ left: getSlideDistance(), behavior: 'smooth' });
    }
  }, [canScrollRight, shouldLoop]);

  useEffect(() => {
    if (!isAutoplayActive || items.length <= 1) return;

    const interval = window.setInterval(scrollRight, 6000);
    return () => window.clearInterval(interval);
  }, [isAutoplayActive, items.length, scrollRight]);

  const handleCardClose = (index: number) => {
    if (carouselRef.current) {
      const cardWidth = isCompactWidth() ? 352 : 448;
      const gap = isCompactWidth() ? 16 : 24;
      const scrollPosition = getLoopSpan() + (cardWidth + gap) * index;
      carouselRef.current.scrollTo({ left: scrollPosition, behavior: 'smooth' });
      setCurrentIndex(index);
    }
  };

  const renderedItems = shouldLoop
    ? [0, 1, 2].flatMap((loopIndex) => items.map((item, index) => ({ item, index, loopIndex })))
    : items.map((item, index) => ({ item, index, loopIndex: 0 }));
  const isPreviousDisabled = !shouldLoop && !canScrollLeft;
  const isNextDisabled = !shouldLoop && !canScrollRight;

  return (
    <CarouselContext.Provider
      value={{
        onCardClose: handleCardClose,
        currentIndex,
        suspendAutoplayForOverlay,
      }}
    >
      <div ref={autoplayViewportRef} className="relative w-full min-h-0 overflow-hidden">
        <div
          className={cn(
            'w-full snap-x snap-mandatory overflow-x-auto overflow-y-clip touch-pan-y scroll-smooth scrollbar-none',
            !isCompactTouch && 'lg:snap-none lg:overflow-x-hidden',
          )}
          ref={carouselRef}
          onScroll={scheduleNormalizeScrollPosition}
        >
          <div className="pointer-events-none absolute right-0 z-10 h-full max-h-full w-[5%] overflow-hidden bg-linear-to-l" />
          <div
            ref={trackRef}
            className="flex w-max flex-row justify-start gap-4 sm:gap-6 xl:w-full"
          >
            {renderedItems.map(({ item, index, loopIndex }) => (
              <div
                key={`card-${loopIndex}-${index}`}
                className={cn(
                  'w-[calc(100vw-2rem)] shrink-0 snap-center rounded-3xl',
                  !isCompactTouch &&
                    'lg:w-auto lg:last:pr-[33%] xl:w-[calc((100%-3rem)/3)] xl:last:pr-0',
                )}
              >
                {cloneElement(item, { instanceKey: `${loopIndex}-${index}` })}
              </div>
            ))}
          </div>
        </div>
        {showControls ? (
          <div className="mx-auto flex max-w-7xl items-end justify-start gap-1 min-[1324px]:justify-end pt-4 sm:pt-6">
            <CarouselButton
              icon={ChevronLeft}
              label="Scroll left"
              onClick={scrollLeft}
              disabled={isPreviousDisabled}
            />
            {!isCompactTouch ? (
              <button
                onClick={() => {
                  setIsPlaying((playing) => {
                    vaTrack(VA_EVENTS.CAROUSEL_PLAY_TOGGLE, {
                      section: 'recent_works',
                      playing: !playing,
                    });
                    return !playing;
                  });
                }}
                aria-label={isPlaying ? 'Pause carousel autoplay' : 'Play carousel autoplay'}
                aria-pressed={isPlaying}
                className="relative z-10 flex size-10 items-center justify-center rounded-full bg-[#948B7F] text-white transition-all duration-300 ease-in-out hover:bg-[#BFB7A1] hover:text-white"
              >
                {isPlaying ? (
                  <Pause className="size-5 shrink-0 fill-white group-hover:fill-foreground transition-all duration-300 ease-in-out" />
                ) : (
                  <Play className="size-5 shrink-0 translate-x-px fill-white group-hover:fill-foreground transition-all duration-300 ease-in-out" />
                )}
              </button>
            ) : null}
            <CarouselButton
              icon={ChevronRight}
              label="Scroll right"
              onClick={scrollRight}
              disabled={isNextDisabled}
            />
          </div>
        ) : null}
      </div>
    </CarouselContext.Provider>
  );
}
